import { BelatedThrottler, Droppable, executeOnce } from '../../../utils'

import {
    Capability,
    type ConnectwareError,
    createIsAuthenticatedWithCapabilitiesSelector,
    createSandboxSelector,
    RuleEngineType,
    selectSandbox,
} from '../../../domain'

import { initialState } from '../..'
import { BaseSandboxUsecase } from './Base'

const selectType = createSandboxSelector('type')
const selectEndpointsSearch = createSandboxSelector('endpointsSearch')
const canReadTopicsMetadata = createIsAuthenticatedWithCapabilitiesSelector(Capability.RULE_ENGINE_RESOURCES)

export class StartSandboxUsecase extends BaseSandboxUsecase {
    private readonly getThrottle = executeOnce(() => this.configurationService.getInteractionThrottle())

    private async updateEndpoints (droppable: Droppable): Promise<void> {
        const search = selectEndpointsSearch(this.getState())

        const endpoints = await this.ruleEngineService.fetchEndpoints(search).catch((e: ConnectwareError) => e)

        if (droppable.isDropped || selectEndpointsSearch(this.getState()) !== search) {
            return
        }

        this.setSandbox({ endpoints })
    }

    invoke (): VoidFunction {
        const droppable = new Droppable()

        if (canReadTopicsMetadata(this.getState())) {
            const throttler = new BelatedThrottler(this.getThrottle())

            droppable.onDrop(
                this.subscribeToState(
                    /**
                     * @returns false (i.e. state is considered changed) if
                     *  - User went to the endpoint mode
                     *  - User changed the search while in endpoint mode
                     */
                    (previous, current) =>
                        !(
                            (selectType(previous) !== RuleEngineType.ENDPOINT && selectType(current) === RuleEngineType.ENDPOINT) ||
                            (selectType(current) === RuleEngineType.ENDPOINT && selectEndpointsSearch(previous) !== selectEndpointsSearch(current))
                        ),
                    // eslint-disable-next-line no-void
                    () => droppable.onDrop(throttler.run(() => void this.updateEndpoints(droppable)))
                )
            )
        }

        /** Reset to init state */
        droppable.onDrop(() => this.setSandbox(selectSandbox(initialState)))

        return () => droppable.drop()
    }
}
